import React, { useEffect, useState, useCallback } from "react";
import { useRefreshRead } from "@/hooks/useRefreshRead";
import { LedgerTable } from "@/components/v3/LedgerTable";
import { HistoricalBookBasis } from "@/components/v3/HistoricalBookBasis";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatNumber, formatINRFull } from "@/lib/format";
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Download, Archive } from "lucide-react";

export default function ClosedLoans() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("sanction_amount");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const params = { search, bucket: "Closed" };

  const fetchLoans = useCallback(() => {
    setLoading(true);
    api.get("/portfolio/loans", { params: { ...params, sort_by: sortBy, sort_dir: sortDir, page, page_size: 20 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, sortBy, sortDir, page]);

  useEffect(() => { const t = setTimeout(fetchLoans, 300); return () => clearTimeout(t); }, [fetchLoans]);
  useRefreshRead(fetchLoans);
  useEffect(() => { setPage(1); }, [search]);

  const exportCsv = async () => {
    const res = await api.get("/portfolio/export", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "closed_loans.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 20));

  const columns = [
    { key: "facility", label: "Facility", sortable: false },
    { key: "borrower", label: "Borrower", sortable: false },
    { key: "product", label: "Product", sortable: false },
    { key: "geography", label: "State", sortable: false },
    { key: "sanction_amount", label: "Sanctioned", sortable: true, align: "right" },
    { key: "roi", label: "ROI", sortable: true, align: "right" },
    { key: "date_of_disbursement", label: "Disbursed", sortable: false },
    { key: "end_date", label: "Closed / End", sortable: false },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="closed-loans-page">
      <header className="mb-6">
        <div className="flex items-center gap-2.5">
          <Archive size={26} className="text-slate-500" />
          <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">Closed Loans</h1>
        </div>
        <p className="text-slate-500 mt-1">{formatNumber(data.total)} closed facilities · settled / fully repaid (excluded from active DPD aging)</p>
      </header>
      <HistoricalBookBasis prefix="closed-loans" />

      <div className="rf-card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="closed-loans-search"
              placeholder="Search borrower or facility no…"
              className="w-full pl-10 pr-4 py-2.5 rounded-sm border border-slate-300 text-sm focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition"
            />
          </div>
          <button onClick={exportCsv} data-testid="closed-export-csv-btn" className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm bg-[#b3122a] text-white text-sm font-semibold hover:bg-[#8c0d20] transition"><Download size={15} /> Export CSV</button>
        </div>
      </div>

      <div className="rf-card overflow-hidden">
        <div className="overflow-x-auto">
          <LedgerTable labels={columns.map(c => c.label)} className="w-full text-sm" data-testid="closed-loans-table">
            <thead>
              <tr className="bg-[#111111] text-slate-300">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && toggleSort(col.key)}
                    className={`px-4 py-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"} ${col.sortable ? "cursor-pointer select-none hover:text-white" : ""}`}
                  >
                    <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "flex-row-reverse" : ""}`}>
                      {col.label}
                      {col.sortable && <ArrowUpDown size={12} className={sortBy === col.key ? "text-[#b3122a]" : "opacity-40"} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400" data-testid="closed-loans-empty">No closed facilities found.</td></tr>}
              {!loading && data.items.map((l) => (
                <tr key={l.id} onClick={() => navigate(`/portfolio/${l.id}`)} className="rf-table-row border-t border-slate-100" data-testid={`closed-loan-row-${l.facility}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{l.facility}</td>
                  <td className="px-4 py-3 font-semibold text-[#1a1a1b] whitespace-nowrap">{l.borrower}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.product}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.geography}</td>
                  <td className="px-4 py-3 text-right text-slate-600 whitespace-nowrap" data-testid={`closed-sanction-${l.id}`}>{formatINRFull(l.sanction_amount)}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{l.roi}%</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.date_of_disbursement || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.end_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </LedgerTable>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-sm text-slate-500">Page {data.page} of {totalPages} · {formatNumber(data.total)} closed facilities</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="closed-page-prev" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1"><ChevronLeft size={16} /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="closed-page-next" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
